/**
 * Bottom-anchors the PDP information pane while it is sticky.
 *
 * The problem this solves. The pane is ~1220px against a 700px viewport, and the
 * media gallery beside it is ~4400px, so the pane has to stay on screen for the
 * length of that gallery — that is the theme's existing `enable_sticky_info`
 * behaviour and it is wanted. But `position: sticky; top: 0` on a box TALLER than
 * the viewport pins its top and its own bottom never comes into view: measured,
 * the add-to-cart sat at a constant 827px in a 700px viewport at every scroll
 * position. Pinning the top keeps the title on screen and the button off it.
 *
 * `bottom: 0` is NOT the fix, and the reason is worth writing down because it
 * looks like it should be. A sticky offset only constrains the element from
 * moving PAST that edge — `bottom: 0` shifts an element UP when its natural
 * bottom sits below the line, and once the element has scrolled above the line
 * the constraint is already satisfied and nothing holds it. So `bottom: 0` cannot
 * keep a box from scrolling off the top; it is the wrong half of the axis.
 *
 * What actually bottom-anchors a too-tall sticky box is a NEGATIVE top offset:
 * `top: viewportHeight - paneHeight`. The pane then pins with its top above the
 * viewport and its bottom flush with the viewport bottom, which is where the size
 * picker, the add-to-cart and the action band live. That value depends on two
 * runtime heights, so it cannot be written in CSS — hence this file.
 *
 * Fallback discipline: the CSS reads `var(--pdp-pane-sticky-top, 0px)`, so before
 * this runs — or if it never runs — the pane behaves exactly as the theme does
 * today (`top: 0`). The property is only ever narrowed from a working default to
 * a better one, never from nothing, so there is no invalid-at-computed-value-time
 * state and no layout jump.
 */
class PdpPaneAnchor {
  constructor(pane) {
    this.pane = pane;
    this.update = this.update.bind(this);

    this.update();

    // The pane's height is not static: opening a tab, expanding a read-more, or
    // revealing a cross-sell size strip all change it, and so does a resize.
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(this.update);
      this.observer.observe(this.pane);
    }
    window.addEventListener('resize', this.update, { passive: true });

    // The pane's height also changes on SCROLL, not only on layout: the sticky
    // header hides and the pane's header clearance drops from 76px to 0 (see
    // sections/header.liquid). A ResizeObserver alone left the anchor computed
    // against the taller pane, which showed as a 76px gap under the band. Cheap
    // rAF-throttled recompute — update() is a read plus a no-op when unchanged.
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  onScroll = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  };

  /**
   * Keeps the pane's first content clear of the reappearing sticky header.
   *
   * Bottom-anchoring sacrifices the top of the pane by design — that is the
   * trade that keeps the add-to-cart on screen. The sacrifice is only UGLY in a
   * narrow band: when the pane overflows the viewport by less than roughly the
   * title's own offset, the title lands PARTIALLY behind the opaque header and
   * is sliced mid-glyph. Overflow much larger than that and the title is fully
   * above the viewport, which reads as normal scrolling; overflow at zero or
   * below and it is fully clear. So only the straddle needs correcting.
   *
   * The correction is cheap precisely where it is needed: the shift required to
   * clear the header is (overflow - titleOffset + headerHeight), and inside the
   * straddle band the overflow is small by construction — measured 5px at a
   * 900px viewport. Outside the band we leave the anchor alone, which is what
   * keeps the shift from ever costing the cross-sell its place above the button
   * at short viewports.
   *
   * Why moving the pane down does NOT push the add-to-cart off screen, which is
   * the obvious fear and the reason this looked impossible: the band is
   * `position: sticky; bottom: 0`, so it pins to the VIEWPORT bottom regardless
   * of where the pane's top sits. Measured at a 700px viewport: shifting the
   * pane down 205px left the band at 537-700 and the add-to-cart at 614-664,
   * both byte-identical to before the shift.
   *
   * The header height is taken as a CONSTANT (its full height, not its current
   * position) on purpose. Guarding only while the header is actually revealed
   * would re-shift the pane on every hide/show, which is both visible jitter and
   * the same oscillation that made the old padding-based clearance cancel out.
   */
  /**
   * Is the sticky header REVEALED and overlaying the page right now?
   *
   * StickyHeader (sections/header.liquid) flags its own state on <body>: it is
   * overlaying content while `shopify-body-section-header-sticky` is set, and
   * hidden while `shopify-body-section-header-hidden` is set alongside it. It
   * hides on scroll-down and reveals on scroll-up, so "revealed" is exactly the
   * scroll-up state where it can collide with the pane's title.
   *
   * Reading the header's own flags rather than tracking scroll direction here
   * keeps the two in step by construction — including the cases StickyHeader
   * treats specially, such as a jump straight back to the top.
   */
  headerRevealed() {
    const body = document.body.classList;
    return (
      body.contains('shopify-body-section-header-sticky') &&
      !body.contains('shopify-body-section-header-hidden')
    );
  }

  clearHeaderStraddle(offset) {
    const title = this.pane.querySelector('.product__title');
    if (!title) return offset;

    // Shift ONLY while the header is actually revealed — i.e. on scroll-up.
    //
    // The geometry here is a genuine conflict, so it is worth stating exactly.
    // Moving the pane DOWN is the only way to clear the title from under the
    // header: a bottom-anchored pane cannot be cleared from above, because
    // anything added at its top simply scrolls out of view. But the band is
    // `sticky; bottom: 0`, so pushing the pane's bottom X below the viewport makes
    // the band float UP by X and paint over X of the content above it. Measured at
    // 1590x850: the title sits 32px behind the header, while the slack between the
    // cross-sell and the band is only 20px — the pane is 910px in an 850px
    // viewport, so there is no spare room. Something has to give.
    //
    // What resolves it is TIME, not space. Unconditional, the shift buried the
    // cross-sell size chip permanently — the reported bug, because the pane is
    // pinned and the chip could never be scrolled out from under the band.
    // Conditional on the header being revealed, the cost becomes transient and
    // self-correcting: scroll up and the title clears; scroll down and the header
    // hides, the pane re-anchors, and the chip is back. The reading state — at
    // rest, or scrolling down through the gallery — always shows the chip.
    //
    // Do NOT try to "pay" for the shift by reserving space above the band. Tried,
    // measured, does not converge: the reserve makes the pane taller, which pushes
    // the anchor further up, which moves the title further under the header, which
    // demands more shift. Each unit of reserve costs a unit of shift and the net
    // clearance stays zero. The earlier attempt reserved the band's full height
    // instead and left a 181px hole.
    if (!this.headerRevealed()) return offset;

    const headerHeight =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      ) || 76;

    // Layout-relative: both rects carry the current sticky offset, so the
    // difference is the title's position INSIDE the pane and is independent of
    // whatever offset we are currently applying.
    const titleRect = title.getBoundingClientRect();
    const paneRect = this.pane.getBoundingClientRect();
    const titleOffset = titleRect.top - paneRect.top;

    // Breathing room under the header. Flush against it (gap 0) is legible but
    // reads as cramped. The theme's own rhythm above the title is 28px (the pane
    // reserves the header height as padding, then spaces the title by 28), but
    // taking the full 28 is equivalent to not anchoring at all, and that is
    // expensive: with the pane at its natural top, the content just above the
    // band becomes permanently unreachable -- it is covered by the pinned band
    // AND the pane is sticky, so it cannot be scrolled out from under it.
    // Measured at an 800px viewport: ~266px buried, which is the cross-sell.
    // 24px buys the air at a cost of exactly 24px of that content.
    const HEADER_GAP = 24;
    const clearance = headerHeight + HEADER_GAP;

    const titleTop = offset + titleOffset;
    const titleBottom = titleTop + titleRect.height;

    // Fully above the viewport, or already clear of the header: nothing to do.
    // The test uses the same clearance as the correction, so a title sitting a
    // few px below the header still gets nudged to the full gap rather than
    // being left cramped.
    if (titleBottom <= 0 || titleTop >= clearance) return offset;

    // Straddling (or cramped). Push down to sit a clear gap under the header,
    // never past the pane's natural top position.
    return Math.min(0, clearance - titleOffset);
  }

  /**
   * Makes the pane reach the viewport bottom when its content is SHORTER than
   * the viewport, so the band actually lands on the bottom edge.
   *
   * The bug this fixes, which is the exact mirror of the trap documented at the
   * top of this file. `position: sticky; bottom: 0` only ever pulls an element
   * UP, when its natural position would put it past the line. It never pushes an
   * element DOWN. So once the pane is shorter than the viewport, the band's
   * natural position is already above the bottom edge, the sticky offset has
   * nothing to do, and the band simply sits wherever the pane happens to end.
   * Measured at a 1000px viewport: pane 911px, band floating 89px clear of the
   * bottom at every scroll position. That is the "band rides up when zoomed out"
   * report -- zooming out enlarges the CSS viewport until the pane fits inside
   * it, which is precisely when this kicks in.
   *
   * The fix is to give the pane the leftover height rather than to reposition
   * the band: the band stays a normal in-flow sticky child, so its full-bleed
   * geometry and its place in the column are untouched. Deliberately NOT done by
   * converting the pane to a flex column with `margin-top: auto` -- the pane is
   * `display: block` and every child is laid out as a block, so flexing it would
   * change margins, widths and collapsing behaviour throughout the column for a
   * bottom-edge problem.
   *
   * The measurement has to subtract the fill we ourselves applied, or the next
   * pass reads its own output as content height and the value oscillates.
   */
  fillToViewport(viewport) {
    const applied = this.lastFill || 0;
    const contentHeight = this.pane.offsetHeight - applied;

    // Only the desktop rule consumes the fill — below 750px the band is
    // `position: fixed`, already sits on the bottom edge, and is out of flow, so
    // there is nothing to stretch. Publishing a fill there would ALSO corrupt the
    // measurement above: `applied` would be subtracted from a height that never
    // included it, understating the content and inflating the next fill, which
    // compounds every pass (observed: 1170px at a 390px viewport). Gate the write
    // on the same breakpoint as the rule so the two never disagree.
    const consumed = window.matchMedia('(min-width: 750px)').matches;
    const fill = consumed ? Math.max(0, viewport - contentHeight) : 0;

    if (fill !== this.lastFill) {
      this.pane.style.setProperty('--pdp-pane-fill', `${Math.round(fill)}px`);
      this.lastFill = fill;
    }
    return contentHeight + fill;
  }

  update() {
    const viewport = window.innerHeight;

    // Stretch first: the anchor below reasons about the pane's FINAL height.
    const paneHeight = this.fillToViewport(viewport);

    // Only bottom-anchor when the pane genuinely overflows the viewport. When it
    // fits, 0 is correct and gives the ordinary top-pinned behaviour.
    let offset = Math.min(0, viewport - paneHeight);
    offset = this.clearHeaderStraddle(offset);

    // Round to avoid churning the property on sub-pixel resize noise.
    const next = `${Math.round(offset)}px`;
    if (next !== this.lastValue) {
      this.pane.style.setProperty('--pdp-pane-sticky-top', next);

      // Animate every change EXCEPT the first. The initial anchor is a layout
      // decision, not a movement — animating it would show the pane sliding into
      // position on every page load. Same reasoning as the header's own `.animate`
      // class, which StickyHeader adds on reveal rather than at boot. Adding it on
      // the frame AFTER the first write keeps that write outside the transition.
      if (this.lastValue !== undefined) {
        this.pane.classList.add('pdp-pane--animate');
      } else {
        requestAnimationFrame(() => this.pane.classList.add('pdp-pane--animate'));
      }

      this.lastValue = next;
    }
  }
}

const initPdpPaneAnchor = () => {
  document
    .querySelectorAll('.product__info-container--has-action-zone')
    .forEach((pane) => {
      if (pane.dataset.paneAnchored === 'true') return;
      pane.dataset.paneAnchored = 'true';
      new PdpPaneAnchor(pane);
    });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPdpPaneAnchor);
} else {
  initPdpPaneAnchor();
}
